import { useEffect, useMemo, useState } from 'react';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { SOC } from '../../constants/company';
import { DEVISE, fmt } from '../../lib/currency';
import {
  appliqueInventaire,
  bilanInventaire,
  ecritBrouillon,
  effaceBrouillon,
  heureCourante,
  lignesInventaire,
  litBrouillon,
} from '../../lib/inventaire';
import { printDoc } from '../../lib/print';
import { today } from '../../lib/utils';
import { C } from '../../theme/palette';

const TOUCHES = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'];

/**
 * Pavé numérique à l'écran, pour compter au téléphone sans lâcher le carton.
 *
 * <p>Le clavier du téléphone couvre la moitié de l'écran et masque la ligne
 * que l'on compte ; ces touches, larges, restent sous la liste.
 *
 * @param {object} props propriétés
 * @param {(touche: string) => void} props.onTouche frappe d'une touche
 * @param {Function} props.onSuivant passe à l'article suivant
 * @param {Function} props.onFermer replie le pavé
 * @returns {import('react').ReactElement} le pavé
 */
function PaveNumerique({ onTouche, onSuivant, onFermer }) {
  const touche = {
    padding: '12px 0',
    textAlign: 'center',
    fontSize: 17,
    fontWeight: 800,
    borderRadius: 9,
    border: '1px solid ' + C.border,
    background: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    minHeight: 44,
  };
  return (
    <div data-testid="pave-numerique" style={{ marginTop: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {TOUCHES.map((t) => (
          <div
            key={t}
            role="button"
            aria-label={t === '⌫' ? 'Effacer' : 'Touche ' + t}
            onClick={() => onTouche(t)}
            style={touche}
          >
            {t}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <div style={{ flex: 1 }}>
          <Btn label="Article suivant ➜" color={C.navy} full onClick={onSuivant} />
        </div>
        <Btn label="Fermer" ghost color={C.grey} onClick={onFermer} />
      </div>
    </div>
  );
}

const ligneEntete = { display: 'flex', justifyContent: 'space-between', fontSize: 11 };

/**
 * Inventaire par comptage : la quantité théorique, ce que l'on trouve, l'écart.
 *
 * <p>Chaque article se compte ; l'écart se calcule et se valide en un bloc :
 * un mouvement d'ajustement par article écarté, sous le motif « inventaire »,
 * et une fiche récapitulative à imprimer et à classer. Le comptage en cours
 * reste sur l'appareil tant qu'il n'est pas validé.
 *
 * @param {object} props propriétés
 * @param {Array} props.stock articles du stock
 * @param {Function} props.setStock modifie le stock
 * @param {object} props.soc société
 * @param {boolean} props.voitPrixAchat vrai pour qui lit les prix d'achat
 * @param {string} [props.utilisateur] qui compte, pour le mouvement
 * @returns {import('react').ReactElement} l'écran
 */
export function Inventaire({ stock, setStock, soc, voitPrixAchat, utilisateur }) {
  const socId = soc?.id ?? soc?.codeSociete ?? null;
  const [brouillon] = useState(() => litBrouillon(socId));
  const [repris, setRepris] = useState(brouillon ? null : true);
  const [comptes, setComptes] = useState({});
  const [commentaire, setCommentaire] = useState('');
  const [recherche, setRecherche] = useState('');
  const [actif, setActif] = useState(null);
  const [fiche, setFiche] = useState(null);

  // Le comptage entame survit a un onglet ferme : il reste sur l'appareil, et
  // ne touche au stock qu'a la validation.
  useEffect(() => {
    if (repris === null) return;
    ecritBrouillon(socId, { comptes, commentaire });
  }, [socId, comptes, commentaire, repris]);

  const lignes = useMemo(() => lignesInventaire(stock, comptes), [stock, comptes]);
  const bilan = bilanInventaire(lignes);
  const filtre = recherche.trim().toLowerCase();
  const visibles = lignes.filter(
    (l) =>
      !filtre ||
      (l.art.nom || '').toLowerCase().includes(filtre) ||
      (l.art.ref || '').toLowerCase().includes(filtre) ||
      (l.art.cat || '').toLowerCase().includes(filtre),
  );

  const fixe = (id, valeur) => {
    const brut = String(valeur)
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    setComptes((p) => ({ ...p, [id]: brut }));
  };
  const frappe = (t) => {
    if (actif == null) return;
    const courant = String(comptes[actif] ?? '');
    if (t === '⌫') return fixe(actif, courant.slice(0, -1));
    if (t === '.' && courant.includes('.')) return undefined;
    return fixe(actif, courant + t);
  };
  const suivant = () => {
    const i = visibles.findIndex((l) => l.art.id === actif);
    const prochain = visibles[i + 1];
    setActif(prochain ? prochain.art.id : null);
  };

  const valide = () => {
    const ecartees = lignes.filter((l) => l.compte != null && l.ecart !== 0);
    const heure = heureCourante();
    setStock((p) =>
      appliqueInventaire(p, lignes, { commentaire, utilisateur, date: today(), heure }),
    );
    setFiche({
      date: today(),
      heure,
      utilisateur: utilisateur || '',
      commentaire,
      comptes: bilan.comptes,
      lignes: ecartees,
      valeur: bilan.valeur,
    });
    effaceBrouillon(socId);
    setComptes({});
    setCommentaire('');
    setActif(null);
    setRepris(true);
  };

  if (repris === null) {
    return (
      <Card style={{ marginBottom: 11, border: '2px solid ' + C.teal }}>
        <Bar
          icon="📋"
          title="Comptage en cours"
          color={C.teal}
          sub={'Commencé le ' + brouillon.date}
        />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
            Un comptage de {Object.keys(brouillon.comptes).length} article(s) est resté sur cet
            appareil. Le reprendre, ou repartir de zéro ?
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label="↩ Reprendre le comptage"
                color={C.teal}
                full
                onClick={() => {
                  setComptes(brouillon.comptes);
                  setCommentaire(brouillon.commentaire);
                  setRepris(true);
                }}
              />
            </div>
            <Btn
              label="Repartir de zéro"
              ghost
              color={C.grey}
              onClick={() => {
                effaceBrouillon(socId);
                setRepris(true);
              }}
            />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {fiche && (
        <Card style={{ marginBottom: 11, border: '2px solid ' + C.green }}>
          <div style={{ padding: 13 }}>
            <style>
              {
                '@media print{body *{visibility:hidden}#fiche-inventaire,#fiche-inventaire *{visibility:visible}#fiche-inventaire{position:absolute;left:0;top:0;width:100%}.noprint-inv{display:none!important}}'
              }
            </style>
            <div
              id="fiche-inventaire"
              style={{
                fontFamily: 'monospace',
                background: '#fff',
                color: '#111',
                padding: '12px 14px',
                border: '1px solid ' + C.border,
                borderRadius: 6,
              }}
            >
              <div style={{ textAlign: 'center', fontWeight: 900, fontSize: 13 }}>
                FICHE D'INVENTAIRE
              </div>
              <div style={{ textAlign: 'center', fontSize: 11, marginBottom: 8 }}>
                {soc?.nom || SOC.nom} · {fiche.date} {fiche.heure}
                {fiche.utilisateur ? ' · ' + fiche.utilisateur : ''}
              </div>
              <div
                style={{
                  borderTop: '1px dashed #999',
                  borderBottom: '1px dashed #999',
                  padding: '5px 0',
                  fontSize: 11,
                }}
              >
                <div style={ligneEntete}>
                  <span>Articles comptés</span>
                  <b>{fiche.comptes}</b>
                </div>
                <div style={ligneEntete}>
                  <span>Articles avec écart</span>
                  <b>{fiche.lignes.length}</b>
                </div>
                {fiche.commentaire && (
                  <div style={{ fontSize: 11 }}>Commentaire : {fiche.commentaire}</div>
                )}
              </div>
              <div style={{ padding: '6px 0', fontSize: 11 }}>
                {fiche.lignes.length === 0 && (
                  <div style={{ color: '#666' }}>Aucun écart : le stock est conforme.</div>
                )}
                {fiche.lignes.map((l) => (
                  <div
                    key={l.art.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    <span>
                      {l.art.nom} <span style={{ color: '#666' }}>({l.art.ref})</span>
                    </span>
                    <span>
                      {l.theorique} → {l.compte} {l.art.u}
                    </span>
                    <b style={{ color: l.ecart < 0 ? '#b91c1c' : '#047857' }}>
                      {l.ecart > 0 ? '+' : ''}
                      {l.ecart}
                      {voitPrixAchat ? ' (' + fmt(l.valeur) + ')' : ''}
                    </b>
                  </div>
                ))}
              </div>
              {voitPrixAchat && (
                <div
                  style={{
                    ...ligneEntete,
                    borderTop: '1px dashed #999',
                    paddingTop: 6,
                    fontWeight: 900,
                    fontSize: 11,
                  }}
                >
                  <span>Valeur de l'écart (prix d'achat)</span>
                  <span>
                    {DEVISE} {fmt(fiche.valeur)}
                  </span>
                </div>
              )}
              <div style={{ fontSize: 11, marginTop: 8, color: '#666' }}>
                Chaque écart est inscrit dans les mouvements de l'article sous le motif « inventaire
                ».
              </div>
            </div>
            <div className="noprint-inv" style={{ display: 'flex', gap: 8, marginTop: 11 }}>
              <Btn label="🖨 Imprimer la fiche" color={C.navy} full onClick={printDoc} />
              <Btn label="Fermer" color={C.muted} onClick={() => setFiche(null)} />
            </div>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="📋"
          title="Inventaire par comptage"
          color={C.navy}
          sub={today() + ' · ' + bilan.comptes + ' compté(s) · ' + bilan.ecartes + ' écart(s)'}
        />
        <div style={{ padding: 13 }}>
          <input
            aria-label="Rechercher un article à compter"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un article, une référence, un rayon"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '9px 11px',
              fontSize: 12,
              marginBottom: 8,
            }}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 52px 76px 56px',
              gap: 6,
              fontSize: 10,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              padding: '0 2px 4px',
            }}
          >
            <span>Article</span>
            <span style={{ textAlign: 'center' }}>Théor.</span>
            <span style={{ textAlign: 'center' }}>Compté</span>
            <span style={{ textAlign: 'right' }}>Écart</span>
          </div>
          {visibles.length === 0 && (
            <div style={{ fontSize: 11, color: C.muted, padding: '8px 2px' }}>
              Aucun article à compter.
            </div>
          )}
          {visibles.map((l) => {
            const estActif = actif === l.art.id;
            return (
              <div key={l.art.id}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 52px 76px 56px',
                    gap: 6,
                    alignItems: 'center',
                    padding: '6px 2px',
                    borderTop: '1px solid ' + C.border,
                    background: estActif ? C.navyXL : 'transparent',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {l.art.nom}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {l.art.ref}
                      {l.art.cat ? ' · ' + l.art.cat : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
                    {l.theorique}
                  </div>
                  <input
                    aria-label={'Compté ' + l.art.nom}
                    inputMode="decimal"
                    value={comptes[l.art.id] ?? ''}
                    placeholder="—"
                    onFocus={() => setActif(l.art.id)}
                    onChange={(e) => fixe(l.art.id, e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid ' + (estActif ? C.navy : C.border),
                      borderRadius: 8,
                      padding: '9px 4px',
                      fontSize: 16,
                      fontWeight: 800,
                      textAlign: 'center',
                      minHeight: 44,
                    }}
                  />
                  <div
                    style={{
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 800,
                      color:
                        l.compte == null
                          ? C.muted
                          : l.ecart === 0
                            ? C.green
                            : l.ecart < 0
                              ? C.red
                              : C.teal,
                    }}
                  >
                    {l.compte == null ? '—' : (l.ecart > 0 ? '+' : '') + l.ecart}
                  </div>
                </div>
                {estActif && (
                  <PaveNumerique
                    onTouche={frappe}
                    onSuivant={suivant}
                    onFermer={() => setActif(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ marginBottom: 11, border: '2px solid ' + C.navy }}>
        <div style={{ padding: 13 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: voitPrixAchat ? '1fr 1fr 1fr' : '1fr 1fr',
              gap: 6,
              marginBottom: 9,
            }}
          >
            {[
              ['Comptés', String(bilan.comptes), C.navy],
              ['Écarts', bilan.manques + ' manque(s) · ' + bilan.surplus + ' surplus', C.amber],
              ...(voitPrixAchat
                ? [
                    [
                      'Valeur (PA)',
                      DEVISE + ' ' + fmt(bilan.valeur),
                      bilan.valeur < 0 ? C.red : C.green,
                    ],
                  ]
                : []),
            ].map(([lb, v, c]) => (
              <div
                key={lb}
                style={{
                  background: c + '10',
                  border: '1px solid ' + c + '25',
                  borderRadius: 9,
                  padding: '7px 9px',
                }}
              >
                <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>{lb}</div>
                <div style={{ fontSize: 11, fontWeight: 800, color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <input
            aria-label="Commentaire de l'inventaire"
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            placeholder="Commentaire (ex. : inventaire de fin de mois)"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '9px 11px',
              fontSize: 11,
              marginBottom: 9,
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label={
                  bilan.comptes === 0
                    ? "Compte d'abord des articles"
                    : "✓ Valider l'inventaire (" + bilan.ecartes + ' ajustement(s))'
                }
                color={C.green}
                full
                disabled={bilan.comptes === 0}
                onClick={valide}
              />
            </div>
            <Btn
              label="Abandonner"
              ghost
              color={C.grey}
              disabled={bilan.comptes === 0 && !commentaire}
              onClick={() => {
                effaceBrouillon(socId);
                setComptes({});
                setCommentaire('');
                setActif(null);
              }}
            />
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
            Les articles non comptés restent tels quels. Chaque écart validé corrige la quantité de
            l'article et s'inscrit dans ses mouvements sous le motif « inventaire ».
          </div>
        </div>
      </Card>

      {(() => {
        const ajustements = (stock || [])
          .flatMap((a) =>
            (a.sorties || [])
              .filter((s) => s.motif === 'inventaire')
              .map((s) => ({ ...s, art: a })),
          )
          .slice(0, 12);
        if (!ajustements.length) return null;
        return (
          <Card style={{ marginBottom: 11 }}>
            <Bar icon="🧾" title="Derniers ajustements d'inventaire" color={C.grey} />
            <div style={{ padding: '9px 13px' }}>
              {ajustements.map((s, i) => (
                <div
                  key={s.id || i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    padding: '4px 0',
                    borderBottom: '1px solid ' + C.border,
                  }}
                >
                  <span>
                    {s.date}
                    {s.heure ? ' ' + s.heure : ''} · {s.art.nom}
                    {s.empNom ? ' · ' + s.empNom : ''}
                  </span>
                  <b style={{ color: +s.qte > 0 ? C.red : C.teal }}>
                    {+s.qte > 0 ? '−' : '+'}
                    {Math.abs(+s.qte || 0)} {s.art.u}
                  </b>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}
    </div>
  );
}
